import React, { useRef } from 'react';
import { exportChartAsPng, DownloadIcon } from '../utils/exportChart';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#3E68B2', '#38D430', '#e67e22', '#9370db', '#20b2aa', '#e74c3c', '#f39c12'];

function makeFormatTick(dateFrom, dateTo) {
  const days = (dateFrom && dateTo)
    ? (new Date(dateTo) - new Date(dateFrom)) / 86400000
    : 0;
  if (days > 90) return val => { try { return format(new Date(val), "d MMM ''yy"); } catch { return val; } };
  if (days > 7)  return val => { try { return format(new Date(val), 'd MMM');      } catch { return val; } };
  return         val => { try { return format(new Date(val), 'MM/dd HH:mm');       } catch { return val; } };
}

function makeFormatLabel(dateFrom, dateTo) {
  const days = (dateFrom && dateTo)
    ? (new Date(dateTo) - new Date(dateFrom)) / 86400000
    : 0;
  const fmt = days > 90 ? "MMM d yyyy, HH:mm" : "MMM d, HH:mm";
  return label => label ? format(new Date(label), fmt) : '';
}

const CustomTooltip = ({ active, payload, label, unit, formatLabel }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'white', border: '1px solid #dce3ec',
      borderRadius: 6, padding: '8px 12px', fontSize: 11,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 700, color: '#142D55', marginBottom: 4 }}>
        {formatLabel ? formatLabel(label) : (label ? format(new Date(label), 'MMM d, HH:mm') : '')}
      </div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ color: '#6b7a8d', minWidth: 60 }}>{p.dataKey}</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {p.value != null ? Number(p.value).toFixed(3) : '—'}{unit ? ` ${unit}` : ''}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function SensorPanel({ rows, columns, title, unit, height = 180, dateFrom, dateTo }) {
  const cardRef    = useRef(null);
  const formatTick  = makeFormatTick(dateFrom, dateTo);
  const formatLabel = makeFormatLabel(dateFrom, dateTo);

  if (!rows || rows.length === 0) {
    return (
      <div className="card">
        <div className="card-header"><span className="card-title">{title}</span></div>
        <div className="empty-state" style={{ minHeight: height }}>
          <div className="empty-state-icon">📉</div>
          <div className="empty-state-text" style={{ fontSize: 11 }}>No data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" ref={cardRef}>
      <div className="card-header">
        <span className="card-title">{title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={() => exportChartAsPng(cardRef.current, title, dateFrom, dateTo)}
            title="Export as PNG"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: '1px solid #dce3ec',
              borderRadius: 5, padding: '3px 8px', cursor: 'pointer',
              color: '#6b7a8d', fontSize: 11, fontWeight: 500,
            }}
          >
            <DownloadIcon size={12} />
            PNG
          </button>
          {unit && <span className="tag">{unit}</span>}
        </div>
      </div>
      <div className="chart-container" style={{ paddingTop: 12 }}>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={rows} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
            <XAxis
              dataKey="t"
              tickFormatter={formatTick}
              tick={{ fontSize: 9, fill: '#6b7a8d' }}
              tickLine={false}
              axisLine={{ stroke: '#dce3ec' }}
              interval="preserveStartEnd"
              minTickGap={80}
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#6b7a8d' }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <Tooltip content={<CustomTooltip unit={unit} formatLabel={formatLabel} />} />
            <Legend
              iconType="circle"
              iconSize={7}
              wrapperStyle={{ fontSize: 10, paddingTop: 4, paddingLeft: 8 }}
            />
            {columns.map((col, i) => (
              <Line
                key={col}
                type="monotone"
                dataKey={col}
                stroke={COLORS[i % COLORS.length]}
                dot={false}
                strokeWidth={1.5}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
